import { inject, Injectable, DOCUMENT, signal } from '@angular/core';
import { TranslationService } from '../../../services/translation/translation.service';


enum ToastMessageType {
  ERROR = 'error',
  INFO = 'info',
  WARNING = 'warning',
  SUCCESS = 'success',
}

export interface ToastMessage {
  type: ToastMessageType;
  content: any;
  hasComponent: boolean;
  isExpirable: boolean;
  isClicked: boolean;
  callback: any;
  canClickedAllToast?: boolean;
  className?: string;
}

@Injectable({
  providedIn: 'root',
})
export class ToastMessageService {
  private _translate = inject(TranslationService);
  private _document = inject(DOCUMENT);

  /**
   * Time before message deletes
   */
  private _expirationTime = 5 * 1000;

  /**
   * List of visible messages
   */
  public messagesList = signal<ToastMessage[]>([]);

  /**
   * Show ERROR toast message
   *
   * @param content
   * @param translateParams
   * @param hasComponent
   * @param isExpirable
   * @param canClickedAllToast
   * @param className
   */
  public error(content: any, translateParams = null, hasComponent = false, isExpirable = true, canClickedAllToast = true, className = '') {
    return this._showMessage(ToastMessageType.ERROR, content, translateParams, hasComponent, isExpirable, canClickedAllToast, className);
  }

  /**
   * Show INFO toast message
   *
   * @param content
   * @param translateParams
   * @param hasComponent
   * @param isExpirable
   * @param canClickedAllToast
   * @param className
   */
  public info(content: any, translateParams = null, hasComponent = false, isExpirable = true, canClickedAllToast = true, className = '') {
    return this._showMessage(ToastMessageType.INFO, content, translateParams, hasComponent, isExpirable, canClickedAllToast, className);
  }

  /**
   * Show WARNING toast message
   *
   * @param content
   * @param translateParams
   * @param hasComponent
   * @param isExpirable
   * @param canClickedAllToast
   * @param className
   */
  public warning(content: any, translateParams = null, hasComponent = false, isExpirable = true, canClickedAllToast = true, className = '') {
    return this._showMessage(ToastMessageType.WARNING, content, translateParams, hasComponent, isExpirable, canClickedAllToast, className);
  }

  /**
   * Show SUCCESS toast message
   *
   * @param content
   * @param translateParams
   * @param hasComponent
   * @param isExpirable
   * @param canClickedAllToast
   * @param className
   */
  public success(content: any, translateParams = null, hasComponent = false, isExpirable = true, canClickedAllToast = true, className = '') {
    return this._showMessage(ToastMessageType.SUCCESS, content, translateParams, hasComponent, isExpirable, canClickedAllToast, className);
  }

  /**
   * Showing toast message
   *
   * @param type
   * @param content
   * @param translateParams
   * @param hasComponent
   * @param isExpirable
   * @param canClickedAllToast
   * @param className
   * @private
   */
  private _showMessage(
    type: ToastMessageType,
    content: any,
    translateParams: any,
    hasComponent: any,
    isExpirable: any,
    canClickedAllToast: boolean,
    className?: string,
  ) {
    const message: ToastMessage = {
      type,
      content: this._setContent(content, translateParams),
      hasComponent,
      isExpirable,
      isClicked: false,
      canClickedAllToast,
      className,
      callback: () => {
      },
    };

    this.messagesList.update(list => [...list, message]);

    /**
     * Removing created message after time expires
     */
    if (isExpirable) {
      setTimeout(() => {
        this.removeMessage(message);
      }, this._expirationTime);
    }

    return message;
  }

  /**
   * Sets content for toast message
   */
  private _setContent(content: any, translateParams: any) {
    if (typeof content === 'string') {
      return this._translate.translate(content, translateParams);
    } else {
      return content;
    }
  }

  public removeMessage(message: any) {
    message.callback();
    this.messagesList.update(list => list.filter(m => m !== message));
  }

  public hideCookieOneSignal() {
    const root = this._document.documentElement!;
    root.classList.add('no-onesignal');
    root.classList.add('no-cookie-message');
  }

  public showCookieOneSignal() {
    const root = this._document.documentElement!;
    root.classList.remove('no-onesignal');
    root.classList.remove('no-cookie-message');
  }
}
